<script setup>
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import ContactsTable from 'dashboard/components-next/Contacts/ContactsTable/ContactsTable.vue';
import EditContactDialog from 'dashboard/components-next/Contacts/ContactsTable/EditContactDialog.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits(['update:sort']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const editDialogRef = ref(null);
const selectedContact = ref(null);

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const onEditContact = contact => {
  selectedContact.value = contact;
  editDialogRef.value?.open();
};

const onMessageContact = async contactId => {
  try {
    // Fetch contact conversations
    await store.dispatch('contactConversations/get', contactId);

    // Get the conversations for this contact
    const conversations =
      store.getters['contactConversations/getAllConversationsByContactId'](
        contactId
      );

    if (conversations && conversations.length > 0) {
      // Navigate to the most recent conversation
      const latestConversation = conversations[0]; // Already sorted by timestamp
      const path = frontendURL(
        conversationUrl({
          accountId: route.params.accountId,
          id: latestConversation.id,
        })
      );
      router.push({ path });
    } else {
      // No conversations exist - show alert
      useAlert(t('CONTACTS_LAYOUT.TABLE.NO_CONVERSATION'));
    }
  } catch (error) {
    console.error('Failed to fetch contact conversations:', error);
    useAlert(t('CONTACTS_LAYOUT.TABLE.ERROR_FETCHING_CONVERSATION'));
  }
};

const handleSort = sortData => {
  emit('update:sort', sortData);
};

const handleContactUpdated = () => {
  // Refresh contact list if needed
};
</script>

<template>
  <div class="contacts-list-container">
    <div class="contacts-table-shell">
      <ContactsTable
        :contacts="contacts"
        :active-sort="activeSort"
        :active-ordering="activeOrdering"
        @sort="handleSort"
        @view-contact="onClickViewDetails"
        @edit-contact="onEditContact"
        @message-contact="onMessageContact"
      />
    </div>

    <EditContactDialog
      ref="editDialogRef"
      :contact="selectedContact"
      @close="selectedContact = null"
      @updated="handleContactUpdated"
    />
  </div>
</template>

<style scoped>
.contacts-list-container {
  padding: 0 24px 24px;
}

.contacts-table-shell {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
</style>
